import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Plus, Loader2, FileWarning, WifiOff } from 'lucide-react';
import { errorText } from '../health/healthConfig';

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../../components/ui/dialog';
import { PortalDialogContent as DialogContent } from '../PortalDialogContent';
import { usePortalDestination, usePortalDraft } from '../PortalDestinationContext';
import { PortalRequestDestination } from '../PortalIdentity';
import { PortalEmptyState } from '../ui/PortalEmptyState';

const SEVERITY_META = {
  mild:     { label: 'Mild',     cls: 'bg-slate-100 text-slate-600' },
  moderate: { label: 'Moderate', cls: 'bg-amber-100 text-amber-700' },
  severe:   { label: 'Severe',   cls: 'bg-red-100 text-red-700' },
};

export default function PortalAdverseReactionsPage() {
  const { api: portalApi, branch, ensureConnection } = usePortalDestination();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmation, setConfirmation] = useState('');

  const [medicine, setMedicine] = useState('');
  const [batch, setBatch] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState('mild');
  const [onsetDate, setOnsetDate] = useState('');
  const [stillTaking, setStillTaking] = useState(false);
  const [notes, setNotes] = useState('');
  usePortalDraft(medicine.trim() || description.trim() || notes.trim(), saving,
    medicine.trim() ? `adverse reaction report for ${medicine.trim()}` : (description.trim() || notes.trim() ? 'your adverse reaction report' : ''));

  const load = useCallback(async () => {
    setLoading(true); setLoadError('');
    try {
      const r = await portalApi.get('/adverse-reactions');
      setRows(r.data.reports || []);
    } catch (e) {
      console.error('Failed to load adverse reaction reports:', e);
      setLoadError(errorText(e));
    } finally { setLoading(false); }
  }, [portalApi]);
  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setMedicine(''); setBatch(''); setDescription(''); setSeverity('mild');
    setOnsetDate(''); setStillTaking(false); setNotes('');
  };

  const submit = async () => {
    if (!branch) {
      toast.error('Choose a service branch first.');
      return;
    }
    if (!medicine.trim() || !description.trim()) {
      toast.error('Medicine name and what happened are required.');
      return;
    }
    setSaving(true);
    try {
      if (!await ensureConnection()) return;
      const result = await portalApi.post('/adverse-reactions', {
        tenant_id: branch.tenant_id,
        medicine_name: medicine.trim(),
        batch_number: batch.trim() || undefined,
        reaction_description: description.trim(),
        severity,
        onset_date: onsetDate || undefined,
        still_taking: stillTaking,
        notes: notes.trim() || undefined,
      });
      const message = `Reaction report sent to ${result.data.branch_name}.`;
      toast.success(message); setConfirmation(message);
      resetForm();
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send your report');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="portal-adr-page">
      <PortalRequestDestination testId="portal-adr-destination" />
      {confirmation && <p role="status" className="text-sm text-emerald-800" data-testid="portal-adr-confirmation">{confirmation}</p>}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-6 h-6 text-amber-600" />
          <h1 className="text-2xl font-bold text-slate-900">Report a Reaction</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button disabled={!branch} className="bg-amber-600 hover:bg-amber-700" data-testid="portal-adr-new-btn">
              <Plus className="w-4 h-4 mr-1" /> New report
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="portal-adr-dialog">
            <DialogHeader>
              <DialogTitle>Report an adverse drug reaction</DialogTitle>
              <DialogDescription>
                Tell your pharmacy what happened after taking a medicine. If this is a medical
                emergency, call emergency services or go to the nearest hospital right away.
              </DialogDescription>
            </DialogHeader>
            <PortalRequestDestination testId="portal-adr-form-destination" />
            <div className="space-y-3">
              <div>
                <Label>Medicine name</Label>
                <Input value={medicine} onChange={(e) => setMedicine(e.target.value)}
                  placeholder="e.g. Amoxicillin 500mg" data-testid="portal-adr-medicine" />
              </div>
              <div>
                <Label>Batch number (optional)</Label>
                <Input value={batch} onChange={(e) => setBatch(e.target.value)} maxLength={60}
                  placeholder="Printed on the pack, e.g. LOT 23A104" data-testid="portal-adr-batch" />
                <p className="text-xs text-slate-500 mt-1">Helps your pharmacy trace a faulty batch quickly.</p>
              </div>
              <div>
                <Label>What happened?</Label>
                <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe the reaction — e.g. rash, swelling, nausea — and when it started"
                  data-testid="portal-adr-description" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Severity</Label>
                  <Select value={severity} onValueChange={setSeverity}>
                    <SelectTrigger data-testid="portal-adr-severity"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(SEVERITY_META).map(([k, m]) => (
                        <SelectItem key={k} value={k} data-testid={`portal-adr-severity-${k}`}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date it started</Label>
                  <Input type="date" value={onsetDate} onChange={(e) => setOnsetDate(e.target.value)} data-testid="portal-adr-onset-date" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="portal-adr-still-taking" data-testid="portal-adr-still-taking"
                  checked={stillTaking} onChange={(e) => setStillTaking(e.target.checked)} />
                <Label htmlFor="portal-adr-still-taking">I am still taking this medicine</Label>
              </div>
              <div>
                <Label>Anything else? (optional)</Label>
                <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="portal-adr-notes" />
              </div>
              <Button onClick={submit} disabled={saving || !branch} className="w-full bg-amber-600 hover:bg-amber-700" data-testid="portal-adr-submit">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <AlertTriangle className="w-4 h-4 mr-1" />}
                Send report
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader><CardTitle>Your reports</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-slate-500 py-6 text-center" data-testid="portal-adr-loading">Loading your reports…</p>
          ) : loadError ? (
            <PortalEmptyState icon={WifiOff} title="Couldn't load your reports" description={loadError}
              testId="portal-adr-load-error"
              action={<Button variant="outline" size="sm" onClick={load} data-testid="portal-adr-retry">Try again</Button>} />
          ) : rows.length === 0 ? (
            <PortalEmptyState icon={FileWarning} title="No reports yet"
              description="If a medicine ever gives you an unexpected reaction, report it here so your pharmacy knows." />
          ) : (
            <div className="space-y-2">
              {rows.map((r) => {
                const meta = SEVERITY_META[r.severity] || SEVERITY_META.mild;
                return (
                  <div key={r.report_id} className="border rounded-xl p-3" data-testid={`portal-adr-${r.report_id}`}>
                    <div className="flex flex-wrap gap-2 items-center justify-between">
                      <div className="font-medium">{r.medicine_name}</div>
                      <Badge className={meta.cls}>{meta.label}</Badge>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">{r.reaction_description}</p>
                    <div className="text-xs text-slate-500 mt-1">
                      Reported {new Date(r.created_at).toLocaleString()}
                      {r.batch_number && <> · batch {r.batch_number}</>}
                      {r.onset_date && <> · started {r.onset_date}</>}
                      {r.still_taking && <> · still taking it</>}
                    </div>
                    <div className="text-xs text-slate-600 mt-1" data-testid={`portal-adr-location-${r.report_id}`}>{r.branch_name}</div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
